import React, { useState, useEffect } from 'react';
import {
  fetchTasks as apiFetchTasks,
  fetchUsers as apiFetchUsers,
  fetchProjects as apiFetchProjects,
  fetchWorkspaces as apiFetchWorkspaces,
  switchWorkspace as apiSwitchWorkspace,
  createTask as apiCreateTask,
  createProject as apiCreateProject,
  login as apiLogin,
  register as apiRegister,
  verifyEmail as apiVerifyEmail
} from './api';
import TaskTable from './TaskTable';
import TaskForm from './TaskForm';
import ProjectForm from './ProjectForm';
import MobilePile from './components/MobilePile';
import Login from './Login';
import Register from './Register';
import VerifyEmail from './VerifyEmail';
import './App.css';
import AccountView from './components/AccountView';
import logo from './assets/Upload.svg';

const getWorkspaceStorageKey = (baseKey) => {
  const savedSession = localStorage.getItem('pileSession');

  if (!savedSession) {
    return baseKey;
  }

  try {
    const session = JSON.parse(savedSession);
    const workspaceId =
      session?.workspace?._id ||
      session?.workspace?.id ||
      session?.workspace;

    return workspaceId
      ? `${baseKey}:${workspaceId}`
      : baseKey;
  } catch (error) {
    return baseKey;
  }
};

function App() {
  const [tasks, setTasks] = useState(() => {
    const cachedTasks = localStorage.getItem(
      getWorkspaceStorageKey('pileTasksCache')
    );

    if (!cachedTasks) {
      return [];
    }

    try {
      return JSON.parse(cachedTasks);
    } catch (error) {
      localStorage.removeItem(
        getWorkspaceStorageKey('pileTasksCache')
      );
      localStorage.removeItem(
        getWorkspaceStorageKey('pileTasksLastSync')
      );
      return [];
    }
  });
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    return localStorage.getItem('pileSelectedProjectId') || 'all';
  });
  const [taskStatusFilter, setTaskStatusFilter] = useState('all');
  const [fullFilterOpen, setFullFilterOpen] = useState(false);
  const [fullFilterView, setFullFilterView] = useState('root');
  const [fullDateFrom, setFullDateFrom] = useState('');
  const [fullDateTo, setFullDateTo] = useState('');
  const [draftFullDateFrom, setDraftFullDateFrom] = useState('');
  const [draftFullDateTo, setDraftFullDateTo] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [parentTaskId, setParentTaskId] = useState(null);
  const [isSubtask, setIsSubtask] = useState(false);
  const [fastView, setFastView] = useState(() => window.innerWidth < 768); // Fast View por defecto en móvil
  const [accountViewOpen, setAccountViewOpen] = useState(false);
  const [isHeaderShrunk, setIsHeaderShrunk] = useState(false);
  const [authView, setAuthView] = useState('login');
  const [session, setSession] = useState(() => {
    const savedSession = localStorage.getItem('pileSession');
    return savedSession ? JSON.parse(savedSession) : null;
  });

  useEffect(() => {
    if (!session) return;

    fetchTasks();
    fetchUsers();
    fetchProjects();

    apiFetchWorkspaces()
      .then(setWorkspaces)
      .catch((error) => {
        console.error('Error fetching workspaces:', error);
        setWorkspaces([]);
      });
  }, [session]);

  useEffect(() => {
    if (!projects.length) return;
    if (selectedProjectId === 'all') return;

    const projectStillExists = projects.some(
      (project) =>
        String(project._id) === String(selectedProjectId)
    );

    if (!projectStillExists) {
      setSelectedProjectId('all');
      localStorage.setItem('pileSelectedProjectId', 'all');
    }
  }, [projects, selectedProjectId]);

  useEffect(() => {
    localStorage.setItem(
      getWorkspaceStorageKey('pileTasksCache'),
      JSON.stringify(tasks)
    );
  }, [tasks]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;

      setIsHeaderShrunk((current) => {
        if (!current && scrollPosition > 120) {
          return true;
        }

        if (current && scrollPosition < 20) {
          return false;
        }

        return current;
      });
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const fetchTasks = async () => {
    try {
      const savedCursor = localStorage.getItem(
        getWorkspaceStorageKey('pileTasksLastSync')
      );

      let lastSync = null;

      if (savedCursor) {
        try {
          lastSync = JSON.parse(savedCursor);
        } catch (error) {
          localStorage.removeItem(
            getWorkspaceStorageKey('pileTasksLastSync')
          );
        }
      }

      const data = await apiFetchTasks(lastSync);

      setTasks((currentTasks) => {
        let nextTasks;

        if (!lastSync) {
          nextTasks = data;
        } else {
          const tasksById = new Map(
            currentTasks.map((task) => [String(task._id), task])
          );

          data.forEach((task) => {
            const taskId = String(task._id);

            if (task.isDeleted) {
              tasksById.delete(taskId);
            } else {
              tasksById.set(taskId, task);
            }
          });

          nextTasks = Array.from(tasksById.values());
        }

        localStorage.setItem(
          getWorkspaceStorageKey('pileTasksCache'),
          JSON.stringify(nextTasks)
        );

        return nextTasks;
      });

      if (data.length > 0) {
        const latestTask = [...data]
          .filter((task) => task.updatedAt && task._id)
          .sort((a, b) => {
            const dateDifference =
              new Date(a.updatedAt) - new Date(b.updatedAt);

            if (dateDifference !== 0) {
              return dateDifference;
            }

            return String(a._id).localeCompare(String(b._id));
          })
          .at(-1);

        if (latestTask) {
          localStorage.setItem(
            getWorkspaceStorageKey('pileTasksLastSync'),
            JSON.stringify({
              updatedAt: latestTask.updatedAt,
              _id: latestTask._id
            })
          );
        }
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);

      const cursorKey = getWorkspaceStorageKey('pileTasksLastSync');
      const cacheKey = getWorkspaceStorageKey('pileTasksCache');

      const hadCursor = localStorage.getItem(cursorKey);

      if (hadCursor) {
        try {
          localStorage.removeItem(cursorKey);

          const fullData = await apiFetchTasks(null);

          const activeTasks = fullData.filter(
            (task) => !task.isDeleted
          );

          setTasks(activeTasks);

          localStorage.setItem(
            cacheKey,
            JSON.stringify(activeTasks)
          );

          const latestTask = [...activeTasks]
            .filter((task) => task.updatedAt && task._id)
            .sort((a, b) => {
              const dateDifference =
                new Date(a.updatedAt) - new Date(b.updatedAt);

              if (dateDifference !== 0) {
                return dateDifference;
              }

              return String(a._id).localeCompare(String(b._id));
            })
            .at(-1);

          if (latestTask) {
            localStorage.setItem(
              cursorKey,
              JSON.stringify({
                updatedAt: latestTask.updatedAt,
                _id: latestTask._id
              })
            );
          }

          console.log(
            'Sincronización recuperada con carga completa.'
          );
        } catch (recoveryError) {
          console.error(
            'Error recuperando sincronización de tareas:',
            recoveryError
          );
        }
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await apiFetchUsers();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const data = await apiFetchProjects();
      setProjects(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleCreateTask = async (taskData) => {
    try {
      const payload = { ...taskData };

      if (isSubtask && parentTaskId) {
        const parentTask = tasks.find(
          (task) => String(task._id) === String(parentTaskId)
        );

        if (parentTask?.project) {
          payload.project = parentTask.project;
        }
      } else if (
        selectedProjectId !== 'all' &&
        selectedProjectId !== 'choose'
      ) {
        payload.project = selectedProjectId;
      }

      if (!payload.project) {
        delete payload.project;
      }

      if (!payload.responsible) {
        delete payload.responsible;
      }

      if (!payload.validator) {
        delete payload.validator;
      }

      if (!payload.dueDate) {
        delete payload.dueDate;
      }

      if (!payload.parentTask) {
        delete payload.parentTask;
      }

      const newTask = await apiCreateTask(payload);
      setTasks((current) => [...current, newTask]);
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const handleCreateProject = async (projectData) => {
    try {
      const newProject = await apiCreateProject(projectData);

      setProjects((current) => [...current, newProject]);
      setSelectedProjectId(newProject._id);
      localStorage.setItem('pileSelectedProjectId', newProject._id);
      setShowProjectForm(false);
    } catch (error) {
      console.error('Error creating project:', error);
      window.alert(
        error.response?.data?.error ||
        'No fue posible crear el proyecto'
      );
    }
  };

  const projectTasks =
    selectedProjectId === 'all'
      ? tasks
      : tasks.filter(
          (task) => String(task.project) === String(selectedProjectId)
        );

  const visibleTasks = projectTasks.filter((task) => {
    const status = String(task.status || '').trim().toLowerCase();

    if (taskStatusFilter === 'all') return true;
    if (taskStatusFilter === 'completed') return status === 'completada';

    if (taskStatusFilter === 'incomplete') {
      return [
        'nueva',
        'trabajando',
        'bloqueada',
        'pendiente'
      ].includes(status);
    }

    return status === taskStatusFilter;
  });

  const fullViewTasks = visibleTasks.filter((task) => {
    const dueDate = String(task.dueDate || '').slice(0, 10);

    if (fullDateFrom && (!dueDate || dueDate < fullDateFrom)) {
      return false;
    }

    if (fullDateTo && (!dueDate || dueDate > fullDateTo)) {
      return false;
    }

    return true;
  });

  const handleOpenForm = (parentId = null) => {
    setIsSubtask(!!parentId);
    setParentTaskId(parentId);
    setShowForm(true);
  };

  const handleProjectScopeChange = async (event) => {
    const value = event.target.value;

    if (value === 'create-project') {
      setShowProjectForm(true);
      return;
    }

    if (value.startsWith('workspace:')) {
      const workspaceId = value.replace('workspace:', '');

      try {
        const data = await apiSwitchWorkspace(workspaceId);

        const newSession = {
          token: data.token,
          user: data.user,
          workspace: data.workspace
        };

        localStorage.setItem(
          'pileSession',
          JSON.stringify(newSession)
        );
        localStorage.setItem('pileSelectedProjectId', 'all');

        setTasks([]);
        setUsers([]);
        setProjects([]);
        setSelectedProjectId('all');
        setSession(newSession);
      } catch (error) {
        console.error('Error switching workspace:', error);

        window.alert(
          error.response?.data?.error ||
          'No fue posible cambiar de workspace'
        );
      }

      return;
    }

    setSelectedProjectId(value);
    localStorage.setItem('pileSelectedProjectId', value);
  };

  const toggleFastView = () => {
    setFastView(!fastView);
  };



  const handleLogout = () => {
    localStorage.removeItem(
      getWorkspaceStorageKey('pileTasksCache')
    );
    localStorage.removeItem(
      getWorkspaceStorageKey('pileTasksLastSync')
    );
    localStorage.removeItem('pileSession');

    setTasks([]);
    setProjects([]);
    setUsers([]);
    setWorkspaces([]);
    setAccountViewOpen(false);
    setSession(null);
  };

  const handleLogin = async (credentials) => {
    try {
      const data = await apiLogin(credentials);

      const newSession = {
        token: data.token,
        user: data.user,
        workspace: data.workspace
      };

      localStorage.setItem('pileSession', JSON.stringify(newSession));
      setSession(newSession);
    } catch (error) {
      const message =
        error.response?.data?.error ||
        'No fue posible iniciar sesión';

      const loginError = new Error(message);
      loginError.requiresTermsAcceptance =
        Boolean(error.response?.data?.requiresTermsAcceptance);
      loginError.termsVersion =
        error.response?.data?.termsVersion || null;

      throw loginError;
    }
  };

  const handleRegister = async (userData) => {
    try {
      const data = await apiRegister(userData);

      localStorage.setItem('pendingVerificationEmail', data.email);
      setAuthView('verify');
    } catch (error) {
      const message =
        error.response?.data?.error ||
        'No fue posible crear la cuenta';

      throw new Error(message);
    }
  };

  const handleVerifyEmail = async ({ email, code }) => {
    try {
      await apiVerifyEmail({ email, code });

      localStorage.removeItem('pendingVerificationEmail');
      setAuthView('login');
    } catch (error) {
      const message =
        error.response?.data?.error ||
        'No fue posible verificar el correo';

      throw new Error(message);
    }
  };

  if (!session) {
    if (authView === 'register') {
      return (
        <Register
          onRegister={handleRegister}
          onBackToLogin={() => setAuthView('login')}
        />
      );
    }

    if (authView === 'verify') {
      const pendingEmail =
        localStorage.getItem('pendingVerificationEmail') || '';

      return (
        <VerifyEmail
          email={pendingEmail}
          onVerify={handleVerifyEmail}
          onBackToLogin={() => setAuthView('login')}
        />
      );
    }

    return (
      <Login
        onLogin={handleLogin}
        onCreateAccount={() => setAuthView('register')}
      />
    );
  }

  return (
    <div className="App">
      {fastView ? (
        <>
          <header
            className={`header fast-view-header ${isHeaderShrunk ? 'shrunk' : ''}`}
          >
            <div className="project-scope-selector fast-project-selector">
              <select
                value={selectedProjectId}
                onChange={handleProjectScopeChange}
              >
                <option disabled>
                  {session?.workspace?.name || 'Workspace actual'}
                </option>

                <option value="all">Todas mis tareas</option>

                {projects
                  .filter((project) => project.isDefault)
                  .map((project) => (
                    <option key={project._id} value={project._id}>
                      Sin proyecto
                    </option>
                  ))}

                {projects
                  .filter((project) => !project.isDefault)
                  .map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.name}
                    </option>
                  ))}

                <option value="create-project">
                  Crear proyecto...
                </option>

                {workspaces.some((workspace) => !workspace.isCurrent) && (
                  <option disabled>────────────</option>
                )}

                {workspaces
                  .filter((workspace) => !workspace.isCurrent)
                  .map((workspace) => (
                    <option
                      key={workspace.id}
                      value={`workspace:${workspace.id}`}
                    >
                      Cambiar a: {workspace.name}
                    </option>
                  ))}
              </select>
            </div>

            <img src={logo} alt="Pile" className="logo" />

            <div className="header-menu">
              <button
                type="button"
                className="hamburger-button"
                onClick={() => setAccountViewOpen(true)}
                aria-label="Abrir Mi cuenta"
              >
                <span></span>
                <span></span>
                <span></span>
              </button>


            </div>
          </header>

          {accountViewOpen ? (
            <div className="account-workspace-layer">
              <AccountView
                session={session}
                projects={projects}
                selectedProjectId={selectedProjectId}
                onTasksImported={fetchTasks}
                onBack={() => setAccountViewOpen(false)}
                onLogout={handleLogout}
                onOpenFullView={() => {
                  setAccountViewOpen(false);
                  setFastView(false);
                }}
              />
            </div>
          ) : (
            <div className="fast-view-content">
              <div className="fast-header-context">
                <span className="fast-account-greeting">
                  Hola, {session?.user?.name?.split(' ')[0]}
                </span>

                <div className="current-project-name">
                  {selectedProjectId === 'all'
                    ? 'Todas mis tareas'
                    : projects.find(
                        (project) =>
                          String(project._id) === String(selectedProjectId)
                      )?.name || 'Todas mis tareas'}
                </div>
              </div>

              <MobilePile
                tasks={visibleTasks}
                setTasks={setTasks}
                users={users}
                projects={projects}
                taskStatusFilter={taskStatusFilter}
                onTaskStatusFilterChange={setTaskStatusFilter}
                onCreateTask={() => handleOpenForm()}
              />
            </div>
          )}
        </>
      ) : (
        <>
          <div className="header full-view-header">
            <img src={logo} alt="Logo" className="logo" />

            <div className="header-buttons">
              <button
                className="btn-crear-tarea"
                onClick={() => handleOpenForm()}
              >
                Crear nueva tarea
              </button>

              <select
                className="btn-fast-view"
                value={selectedProjectId}
                onChange={handleProjectScopeChange}
              >
                <option disabled>
                  {session?.workspace?.name || 'Workspace actual'}
                </option>

                <option value="all">Todas mis tareas</option>

                {projects
                  .filter((project) => project.isDefault)
                  .map((project) => (
                    <option key={project._id} value={project._id}>
                      Sin proyecto
                    </option>
                  ))}

                {projects
                  .filter((project) => !project.isDefault)
                  .map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.name}
                    </option>
                  ))}

                <option value="create-project">
                  Crear proyecto...
                </option>

                {workspaces.some((workspace) => !workspace.isCurrent) && (
                  <option disabled>────────────</option>
                )}

                {workspaces
                  .filter((workspace) => !workspace.isCurrent)
                  .map((workspace) => (
                    <option
                      key={workspace.id}
                      value={`workspace:${workspace.id}`}
                    >
                      Cambiar a: {workspace.name}
                    </option>
                  ))}
              </select>

              <div className="full-filter-control">
                <button
                  type="button"
                  className="full-filter-button"
                  onClick={() => {
                    setFullFilterView('root');
                    setFullFilterOpen((current) => !current);
                  }}
                  aria-label="Filtrar tareas"
                >
                  <span>Filtrar</span>

                  <span
                    className="full-filter-button-icon"
                    aria-hidden="true"
                  >
                    <svg viewBox="0 0 24 24">
                      <path d="M3 5h18l-7 8v5l-4 2v-7L3 5z" />
                    </svg>
                  </span>
                </button>

                {fullFilterOpen && (
                  <div className="full-filter-popover">
                    {fullFilterView === 'root' ? (
                      <>
                        <div className="full-filter-popover-title">
                          Filtrar tareas
                        </div>

                        <button
                          type="button"
                          className="full-filter-option"
                          onClick={() => setFullFilterView('status')}
                        >
                          <span>Estado</span>
                          <span aria-hidden="true">›</span>
                        </button>

                        <button
                          type="button"
                          className="full-filter-option"
                          onClick={() => {
                            setDraftFullDateFrom(fullDateFrom);
                            setDraftFullDateTo(fullDateTo);
                            setFullFilterView('date');
                          }}
                        >
                          <span>Fecha de entrega</span>
                          <span aria-hidden="true">›</span>
                        </button>
                      </>
                    ) : fullFilterView === 'status' ? (
                      <>
                        <button
                          type="button"
                          className="full-filter-back"
                          onClick={() => setFullFilterView('root')}
                        >
                          ‹ Filtros
                        </button>

                        <div className="full-filter-popover-title">
                          Estado
                        </div>

                        {[
                          ['all', 'Todas las tareas'],
                          ['completed', 'Terminadas'],
                          ['incomplete', 'Incompletas'],
                          ['separator', ''],
                          ['nueva', 'Nueva'],
                          ['trabajando', 'Trabajando'],
                          ['bloqueada', 'Bloqueada'],
                          ['pendiente', 'Pendiente']
                        ].map(([value, label]) =>
                          value === 'separator' ? (
                            <div
                              key="separator"
                              className="full-filter-separator"
                            />
                          ) : (
                            <button
                              type="button"
                              key={value}
                              className={`full-filter-value ${
                                taskStatusFilter === value
                                  ? `selected selected-${value}`
                                  : ''
                              }`}
                              onClick={() => {
                                setTaskStatusFilter(value);
                                setFullFilterOpen(false);
                              }}
                            >
                              <span>{label}</span>

                              {taskStatusFilter === value && (
                                <span aria-hidden="true">✓</span>
                              )}
                            </button>
                          )
                        )}
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="full-filter-back"
                          onClick={() => setFullFilterView('root')}
                        >
                          ‹ Filtros
                        </button>

                        <div className="full-filter-popover-title">
                          Fecha de entrega
                        </div>

                        <div className="full-filter-date-field">
                          <label htmlFor="full-filter-date-from">
                            Desde
                          </label>
                          <input
                            id="full-filter-date-from"
                            type="date"
                            value={draftFullDateFrom}
                            max={draftFullDateTo || undefined}
                            onChange={(event) =>
                              setDraftFullDateFrom(event.target.value)
                            }
                          />
                        </div>

                        <div className="full-filter-date-field">
                          <label htmlFor="full-filter-date-to">
                            Hasta
                          </label>
                          <input
                            id="full-filter-date-to"
                            type="date"
                            value={draftFullDateTo}
                            min={draftFullDateFrom || undefined}
                            onChange={(event) =>
                              setDraftFullDateTo(event.target.value)
                            }
                          />
                        </div>

                        <div className="full-filter-date-actions">
                          <button
                            type="button"
                            className="full-filter-date-clear"
                            onClick={() => {
                              setDraftFullDateFrom('');
                              setDraftFullDateTo('');
                              setFullDateFrom('');
                              setFullDateTo('');
                              setFullFilterOpen(false);
                            }}
                          >
                            Limpiar
                          </button>

                          <button
                            type="button"
                            className="full-filter-date-apply"
                            onClick={() => {
                              setFullDateFrom(draftFullDateFrom);
                              setFullDateTo(draftFullDateTo);
                              setFullFilterOpen(false);
                            }}
                          >
                            Aplicar
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <button type="button" onClick={toggleFastView}>
                Fast View
              </button>

              <div className="full-account-menu">
                <button
                  type="button"
                  className="full-account-button"
                  onClick={() => setAccountViewOpen(true)}
                  aria-label="Mi cuenta"
                >
                  <span>Mi cuenta</span>

                  <span
                    className="full-account-button-icon"
                    aria-hidden="true"
                  >
                    <svg viewBox="0 0 24 24">
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>

            <div className="project-user-row">
              <div className="current-project-name">
                {selectedProjectId === 'all'
                  ? 'Todas mis tareas'
                  : projects.find(
                      (project) => String(project._id) === String(selectedProjectId)
                    )?.name || 'Todas mis tareas'}
              </div>

              <div className="user-greeting">
                Hola, {session?.user?.name}
              </div>
            </div>
          </div>

          <TaskTable
            tasks={fullViewTasks}
            setTasks={setTasks}
            onCreateSubtask={handleOpenForm}
            users={users}
            projects={projects}
          />

          {accountViewOpen && (
            <div className="account-workspace-layer account-workspace-full">
              <AccountView
                session={session}
                projects={projects}
                selectedProjectId={selectedProjectId}
                onTasksImported={fetchTasks}
                onBack={() => setAccountViewOpen(false)}
                onLogout={handleLogout}
              />
            </div>
          )}
        </>
      )}

      {showProjectForm && (
        <ProjectForm
          onSubmit={handleCreateProject}
          onClose={() => setShowProjectForm(false)}
        />
      )}

      {showForm && (
        <TaskForm
          onSubmit={handleCreateTask}
          onClose={() => setShowForm(false)}
          parentTask={parentTaskId}
          isSubtask={isSubtask}
          users={users}
          projects={projects}
          showProjectSelector={selectedProjectId === 'all'}
        />
      )}
    </div>
  );
}

export default App;

